import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/lib/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, currentUser } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && currentUser) {
    const hasRequiredRole = currentUser.roles.some(role => 
      role === UserRole.ADMIN || 
      requiredRoles.includes(role as UserRole)
    );

    if (!hasRequiredRole) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check for location-based access
  if (currentUser) {
    const path = location.pathname;
    
    // Check if path contains location information
    const matches = path.match(/\/([a-z]{2})\/([a-z-]+)\/dashboard/);
    
    if (matches) {
      const [, state, city] = matches;
      const isAdmin = currentUser.roles.includes(UserRole.ADMIN);
      const hasAllLocationsAccess = currentUser.roles.includes(UserRole.ALL_LOCATIONS);
      
      // Check for specific location access
      if (state === 'wa' && city === 'auburn') {
        const hasLocationAccess = currentUser.roles.includes(UserRole.LOCATION_AUBURN_WA);
        if (!isAdmin && !hasAllLocationsAccess && !hasLocationAccess) {
          return <Navigate to="/dashboard" replace />;
        }
      } else if (state === 'tx' && city === 'grand-prairie') {
        const hasLocationAccess = currentUser.roles.includes(UserRole.LOCATION_GRAND_PRAIRIE_TX);
        if (!isAdmin && !hasAllLocationsAccess && !hasLocationAccess) {
          return <Navigate to="/dashboard" replace />;
        }
      }
    }
  }

  return children;
} 